// ── Trello workspace/board scope picker (C1552) ──
//
// Shared chained workspace -> board dropdowns + "current scope" bar, used by BOTH web
// Trello surfaces (components/trello-card-picker.js and components/trello-import-modal.js).
// Deliberately NOT added to lib/import-picker-shell.js: that file is the provider-neutral
// overlay chrome shared with the Gmail and Jira pickers (see its header comment) — putting
// Trello-specific fetches there would pull /trello/* calls into modules that have nothing
// to do with Trello, and risks breaking gmail-thread-picker.test.js / jira-issue-picker.test.js,
// which stub out exactly the shell's current (Trello-free) module graph.
//
// Split into pure string builders (unit-testable, no DOM — see trello-scope-picker.test.js)
// and one DOM-wiring entry point, mirroring components/member-combo.js's shape.
//
// Aliveness: every await is followed by a `root.isConnected` check rather than a
// host-specific `destroyed` flag — this module doesn't know or care whether its host is
// lib/import-picker-shell.js's body (replaced via `body.innerHTML = ...`) or ui.js's
// `#modal-overlay` (removed wholesale by the next openModal() call). Once `root` is
// detached from the document, both hosts have moved on to another step.

import { api } from '../api.js';
import { escapeHtml } from '../utils.js';

const esc = escapeHtml;

// Sentinel workspaceId for boards Trello reports with no organization — mirrors the same
// constant in api/src/routes/trello.js. Kept as a literal (not imported) since web/ and
// api/src/ are separate bundles; the server is the source of truth for which boards this
// value selects.
export const PERSONAL_WORKSPACE_ID = 'personal';

// ── pure, unit-testable ──

// workspaceOptionsHtml(workspaces, selectedId?) -> '<option>' string, placeholder first.
export function workspaceOptionsHtml(workspaces, selectedId = '') {
  const opts = (workspaces || [])
    .map((w) => `<option value="${esc(w.id)}"${w.id === selectedId ? ' selected' : ''}>${esc(w.name)}</option>`)
    .join('');
  return `<option value="">Select workspace…</option>${opts}`;
}

// boardOptionsHtml(boards, selectedId?) -> '<option>' string. Options carry data-name so
// callers can read the human-readable board name off `selectedOptions[0].dataset.name`
// (Trello's board-association row stores that name for display; the id alone isn't one).
export function boardOptionsHtml(boards, selectedId = '') {
  if (!boards || !boards.length) return '<option value="">No open boards found</option>';
  const opts = boards
    .map((b) => `<option value="${esc(b.id)}" data-name="${esc(b.name)}"${b.id === selectedId ? ' selected' : ''}>${esc(b.name)}</option>`)
    .join('');
  return `<option value="">Select board…</option>${opts}`;
}

// matchesCardQuery(card, q) -> bool. Case-insensitive match against name + description; ''
// (or falsy) matches everything. Mirrors the server's GET /trello/cards?search= filter in
// api/src/routes/trello.js so client-side re-filtering (sort/local re-search) agrees with it.
export function matchesCardQuery(card, q) {
  if (!q) return true;
  const needle = q.toLowerCase();
  const name = (card?.name || '').toLowerCase();
  const desc = (card?.desc || '').toLowerCase();
  return name.includes(needle) || desc.includes(needle);
}

// resolveEffectiveScope(association, override) -> { workspaceId, boardId, boardName,
//   persisted, source } | null
//   association — the project_trello_boards row shape: { trello_workspace_id,
//     trello_board_id, trello_board_name } | null
//   override    — a picker-session choice: { workspaceId, boardId, boardName, persisted } |
//     null. `persisted` defaults true (a successful save); an editor's override still goes
//     through this path so "Change" always reflects the latest pick without a re-fetch.
// override always wins when present — it's either a fresh save (now the project default
// too) or a viewer's session-only pick that must not be silently discarded in favor of the
// stale saved association.
export function resolveEffectiveScope(association, override) {
  if (override && override.boardId) {
    return {
      workspaceId: override.workspaceId || '',
      boardId: override.boardId,
      boardName: override.boardName || override.boardId,
      persisted: override.persisted !== false,
      source: 'override',
    };
  }
  if (association && association.trello_board_id) {
    return {
      workspaceId: association.trello_workspace_id || '',
      boardId: association.trello_board_id,
      boardName: association.trello_board_name || association.trello_board_id,
      persisted: true,
      source: 'association',
    };
  }
  return null;
}

// renderTrelloScopeBar({ boardName, boardId, persisted, changeId }) -> html string. Sits
// atop the card list once a board is chosen; `changeId` is the button id the host wires up
// to re-enter the board step.
export function renderTrelloScopeBar({ boardName, boardId, persisted = true, changeId }) {
  const name = esc(boardName || boardId || '');
  const badge = persisted ? '' : '<span class="trello-scope-badge">This session only</span>';
  return `<div class="trello-scope-bar">
    <span class="trello-scope-label">Board</span>
    <span class="trello-scope-name" title="${name}">${name}</span>
    ${badge}
    <button type="button" class="btn btn-ghost btn-sm trello-scope-change" id="${esc(changeId)}">Change</button>
  </div>`;
}

// ── DOM wiring ──

// renderTrelloScopePickers(root, opts) -> void
//   root         — element to fill with the chained selects + footer (host owns swapping
//                  this element out; this module never removes it itself)
//   idPrefix     — element ids are `${idPrefix}-ws|-board|-confirm|-cancel`; hosts differ
//                  (trello-card-picker.js uses 'trello-picker', trello-import-modal.js uses
//                  a plain 'trello-scope' default) so both can wire independent step markup
//   initial      — { workspaceId, boardId } to preselect + preload boards for, e.g. when
//                  re-entering via "Change" on an already-scoped card list
//   confirmLabel / cancelLabel — footer button text ('Back' when re-entered from the scope
//                  bar with a board already chosen, per the callers below)
//   onConfirm(selection) -> Promise   selection = { workspaceId, boardId, boardName }.
//                  Rejecting re-enables the Continue button and forwards to onError — the
//                  normal 403-vs-save-failure branching belongs in this callback (see
//                  commitScope() in both host files), not here; this module only handles the
//                  truly-unexpected-throw case.
//   onCancel()    — Cancel/Back click
//   onError(message, err) — board/workspace load failures, and onConfirm rejections
//   loadWorkspaces / loadBoards — injectable for tests; default to the real endpoints
export function renderTrelloScopePickers(root, {
  idPrefix = 'trello-scope',
  initial = {},
  confirmLabel = 'Continue',
  cancelLabel = 'Cancel',
  onConfirm,
  onCancel,
  onError,
  loadWorkspaces = () => api.get('/trello/workspaces'),
  loadBoards = (wsId) => api.get('/trello/boards?workspaceId=' + encodeURIComponent(wsId)),
} = {}) {
  const wsId = `${idPrefix}-ws`;
  const boardId = `${idPrefix}-board`;
  const confirmId = `${idPrefix}-confirm`;
  const cancelId = `${idPrefix}-cancel`;

  let boardSeq = 0; // discards a stale /boards response if the workspace changes again fast

  root.innerHTML = `<p class="trello-scope-status">Loading workspaces…</p>`;

  loadWorkspaces().then((workspaces) => {
    if (!root.isConnected) return;

    if (!workspaces || !workspaces.length) {
      root.innerHTML = `<p class="trello-scope-status">No Trello workspaces found. Make sure your Trello account has access to at least one workspace.</p>
        <div class="modal-footer"><button type="button" class="btn btn-ghost" id="${esc(cancelId)}">${esc(cancelLabel === 'Back' ? 'Back' : 'Close')}</button></div>`;
      root.querySelector(`#${cancelId}`).addEventListener('click', () => onCancel && onCancel());
      return;
    }

    root.innerHTML = `
      <div class="form-group">
        <label class="form-label" for="${esc(wsId)}">Workspace</label>
        <select class="form-input" id="${esc(wsId)}">${workspaceOptionsHtml(workspaces, initial.workspaceId || '')}</select>
      </div>
      <div class="form-group">
        <label class="form-label" for="${esc(boardId)}">Board</label>
        <select class="form-input" id="${esc(boardId)}" disabled><option value="">Select workspace first</option></select>
      </div>
      <div class="modal-footer">
        <button type="button" class="btn btn-ghost" id="${esc(cancelId)}">${esc(cancelLabel)}</button>
        <button type="button" class="btn btn-primary" id="${esc(confirmId)}" disabled>${esc(confirmLabel)}</button>
      </div>`;

    const wsSelect = root.querySelector(`#${wsId}`);
    const boardSelect = root.querySelector(`#${boardId}`);
    const confirmBtn = root.querySelector(`#${confirmId}`);

    root.querySelector(`#${cancelId}`).addEventListener('click', () => onCancel && onCancel());

    async function fetchBoards(selectedWsId, preselectBoardId) {
      const seq = ++boardSeq;
      confirmBtn.disabled = true;
      boardSelect.disabled = true;
      boardSelect.innerHTML = '<option>Loading…</option>';
      let boards;
      try {
        boards = await loadBoards(selectedWsId);
      } catch (err) {
        if (!root.isConnected || seq !== boardSeq) return;
        boardSelect.innerHTML = '<option value="">Failed to load boards</option>';
        onError && onError(err.message || 'Failed to load boards.', err);
        return;
      }
      if (!root.isConnected || seq !== boardSeq) return;
      boardSelect.innerHTML = boardOptionsHtml(boards, preselectBoardId || '');
      boardSelect.disabled = false;
      confirmBtn.disabled = !boardSelect.value;
    }

    wsSelect.addEventListener('change', () => {
      const selected = wsSelect.value;
      if (!selected) {
        boardSelect.disabled = true;
        boardSelect.innerHTML = '<option value="">Select workspace first</option>';
        confirmBtn.disabled = true;
        return;
      }
      fetchBoards(selected, null);
    });

    // Bound once, outside the workspace-change handler — a duplicate here (the bug in both
    // pre-extraction copies) would stack another listener on every workspace switch.
    boardSelect.addEventListener('change', () => {
      confirmBtn.disabled = !boardSelect.value;
    });

    confirmBtn.addEventListener('click', async () => {
      const selectedWs = wsSelect.value;
      const selectedBoard = boardSelect.value;
      const selectedBoardName = boardSelect.selectedOptions[0]?.dataset.name || '';
      if (!selectedWs || !selectedBoard || !onConfirm) return;
      confirmBtn.disabled = true;
      const prevLabel = confirmBtn.textContent;
      confirmBtn.textContent = 'Saving…';
      try {
        await onConfirm({ workspaceId: selectedWs, boardId: selectedBoard, boardName: selectedBoardName });
      } catch (err) {
        if (!root.isConnected) return;
        confirmBtn.disabled = false;
        confirmBtn.textContent = prevLabel;
        onError && onError(err.message || 'Failed to save board selection.', err);
      }
    });

    // Preload boards when re-entering with a known workspace (the "Change" path) so the
    // board list is populated and pre-selected without a second click.
    if (initial.workspaceId) fetchBoards(initial.workspaceId, initial.boardId || null);
  }).catch((err) => {
    if (!root.isConnected) return;
    root.innerHTML = `<p class="trello-scope-status">${esc(err.message || 'Failed to load workspaces.')}</p>`;
    onError && onError(err.message || 'Failed to load workspaces.', err);
  });
}
